import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import "./AppModal.scss";

/**
 * AppModal — Theme-aware modal dialog (no Bootstrap dependency).
 *
 * Uses CSS variables for light/dark theme support:
 *   --bg-primary, --bg-secondary, --border-primary, --text-primary, --text-secondary
 *
 * Props:
 *   show          — boolean visibility
 *   onHide        — close handler
 *   title         — header text or React node
 *   icon          — optional React node shown left of title
 *   size          — "sm" | "md" | "lg" | "xl" (default "md")
 *   children      — body content (rendered inside scrollable area)
 *   footer        — optional footer node (rendered with separator)
 *   closeOnOverlay — close when backdrop clicked (default true)
 *   closeOnEscape  — close on Escape key (default true)
 *   hideCloseButton — hide the X button (default false)
 *   className      — extra class on dialog
 *   bodyClassName  — extra class on body wrapper
 */
const SIZE_MAP = {
  sm: 420,
  md: 640,
  lg: 880,
  xl: 1100,
};

const AppModal = ({
  show,
  onHide,
  title,
  icon,
  size = "md",
  children,
  footer,
  closeOnOverlay = true,
  closeOnEscape = true,
  hideCloseButton = false,
  className = "",
  bodyClassName = "",
}) => {
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape" && closeOnEscape) onHide?.();
  }, [onHide, closeOnEscape]);

  useEffect(() => {
    if (!show) return undefined;
    document.addEventListener("keydown", handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [show, handleKeyDown]);

  if (!show) return null;

  const maxWidth = SIZE_MAP[size] || SIZE_MAP.md;

  return createPortal(
    <div
      className="app-modal__overlay"
      onClick={closeOnOverlay ? onHide : undefined}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`app-modal__dialog ${className}`}
        style={{ maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || !hideCloseButton) && (
          <div className="app-modal__header">
            <div className="app-modal__title">
              {icon && <span className="app-modal__icon">{icon}</span>}
              {title && <span>{title}</span>}
            </div>
            {!hideCloseButton && (
              <button
                type="button"
                className="app-modal__close"
                onClick={onHide}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            )}
          </div>
        )}
        <div className={`app-modal__body ${bodyClassName}`}>{children}</div>
        {footer && <div className="app-modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default AppModal;
